import { createContext, useContext, useState } from 'react';

import type {
  IModalsProvider,
  ModalConfirmParams,
  ModalMessageParams,
  ModalMount,
  ModalState,
  ModalsContext,
  ShowErrorV2Params,
} from './types';

import useLanguage from '@snowit/system/useLanguage';
import randomId from '@snowit/utils/randomId';

import Modal from './Modals/Modal/Modal';
import ModalConfirm from './Modals/ModalConfirm';
import ModalError from './Modals/ModalError';
import ModalMessage from './Modals/ModalMessage';

export const Context = createContext({} as ModalsContext);
const ModalsStateContext = createContext<Record<string, ModalState>>({});

export const ModalsRenderer = () => {
  const modals = useContext(ModalsStateContext);

  return Object.entries(modals).map(([key, modal]) => {
    switch (modal.type) {
      case 'confirm':
        return <ModalConfirm key={key} {...modal} />;
      case 'error':
        return <ModalError key={key} {...modal} />;
      case 'message':
        return <ModalMessage key={key} {...modal} />;
      default:
        return <Modal key={key} {...modal} />;
    }
  });
};

const ModalsProvider = (props: IModalsProvider) => {
  const { children, renderModals = true } = props;
  const { t, te } = useLanguage();
  const [modals, setModals] = useState<Record<string, ModalState>>({});

  const mount = (
    name: string,
    modal: ModalMount,
    type: 'confirm' | 'error' | 'message' | 'default' = 'default',
    callback?: Function,
  ) => {
    const removeModal = (key: string) => {
      setModals((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          opened: false,
        },
      }));

      if (typeof callback === 'function') {
        callback(name);
      }
    };

    setModals((prev) => ({
      ...prev,
      [name]: {
        ...modal,
        id: name,
        opened: true,
        type,
        onClose: removeModal,
      },
    }));
  };

  const unmount = (name: string) => {
    if (!modals[name]) return;
    if (!modals[name].opened) return;
    if (typeof modals[name].onClose === 'function') {
      modals[name].onClose(name);
    }
  };

  const unmountAll = () => {
    for (const modal of Object.keys(modals)) {
      unmount(modal);
    }
  };

  const showError = (message?: string, _code?: string, text?: string) => {
    const codestr = _code ? te(_code) : '';
    let output = te(message || 'generic');
    let code: string | undefined = output !== message ? message : undefined;
    if (_code) code = _code;
    if (!message && code) output = '';

    mount(
      '_id_error',
      {
        header: t('lzy.modal_error.generic.title'),
        code,
        body: [codestr, output, text]
          .reduce((memo, str) => {
            if (!str) return memo;
            if (memo.includes(str)) return memo;
            return [...memo, str];
          }, [] as string[])
          .join('\n\n'),
      },
      'error',
    );
  };

  const showErrorV2 = (params: string | Partial<ShowErrorV2Params>) => {
    let opts: ShowErrorV2Params;
    if (typeof params === 'string') {
      opts = {
        message: te(params),
        code: params,
        stack: false,
      };
    } else {
      opts = {
        message: te(params.message || params.code || ''),
        code: params.code || '',
        stack: Boolean(params.stack),
      };
    }

    const code = opts.message === opts.code ? '' : opts.code;

    const id = opts.stack ? randomId() : opts.id || 'error';
    const modal: ModalMount = {
      header: t('lzy.modal_error.generic.title'),
      code,
      body: opts.message,
    };
    mount(id, modal, 'error');
  };

  const showMessage = (message: string, params: ModalMessageParams = {}) => {
    mount(
      params.id || 'message',
      {
        header: params.title,
        body: message,
      },
      'message',
    );
  };

  const showConfirm = (params: ModalConfirmParams): Promise<boolean> => {
    return new Promise((resolve) => {
      const id = params.id || 'confirm';
      mount(
        id,
        {
          header: params.header || t('lzy.modal_confirm.generic.title'),
          body: params.body || t('lzy.modal_confirm.generic.text'),
          confirm: {
            cancelLabel: t(params.cancelLabel || 'lzy.action.cancel'),
            confirmLabel: t(params.confirmLabel || 'lzy.action.confirm'),
            onConfirm: async (...args) => {
              await params.onConfirm(...args);
              resolve(true);
            },
            onCancel: async (...args) => {
              if (typeof params.onCancel === 'function') {
                await params.onCancel(...args);
              }
              resolve(false);
            },
          },
          size: params.size,
        },
        'confirm',
        params.callback,
      );
    });
  };

  return (
    <Context.Provider
      value={{
        mount,
        unmount,
        unmountAll,
        showError,
        showErrorV2,
        showMessage,
        showConfirm,
      }}
    >
      <ModalsStateContext.Provider value={modals}>
        {children}
        {renderModals && <ModalsRenderer />}
      </ModalsStateContext.Provider>
    </Context.Provider>
  );
};

export default ModalsProvider;
